'use client'

import React, { FC, ReactNode, useEffect, useState } from 'react'
import { Spinner } from '../Spinner'
import { Text } from '../../Text'

export interface StepToLoad {
    isLoaded: boolean
    content: ReactNode
}

export interface Props {
    steps: StepToLoad[]
    /** This sets the minimum time for each step to be visible.
     * IMPORTANT NOTE: The application always waits the provided amount of milliseconds for each provided step.
     * This is by default set to 300ms so the text per step is "readable".
     * This doesn't block the application from loading though as the "children" get rendered as soon as every step's isLoaded = true.
     */
    minimumLoadTimePerStep?: number
    children: ReactNode
    /** If loading the application takes longer than this time (in milliseconds) there should be a message informing the user to refresh */
    timeoutTime?: number
    timeoutMessage?: string
}

/** Loader used when loading a full application. Steps provide the user with additional information about what is being loaded, i.e. user-data (logging in) or translations (user-interface) */
export const SteppedAppLoader: FC<Props> = ({
    steps,
    minimumLoadTimePerStep = 300,
    timeoutTime = 10000,
    timeoutMessage,
    children,
}) => {
    const [currentStep, setCurrentStep] = useState(0)
    const [hasTimedOut, setHasTimedOut] = useState(false)

    useEffect(() => {
        setTimeout(() => {
            if (!steps.every((step) => step.isLoaded)) setHasTimedOut(true)
        }, timeoutTime)
    }, [])

    useEffect(() => {
        if (currentStep < steps.length && steps[currentStep].isLoaded) {
            const timeout = setTimeout(() => {
                setCurrentStep((currentStepValue) => currentStepValue + 1)
            }, minimumLoadTimePerStep)

            return () => clearTimeout(timeout)
        }
    }, [currentStep, steps, minimumLoadTimePerStep])

    return (
        <>
            {steps.every((step) => step.isLoaded) && children}
            {currentStep < steps.length && (
                <div className="flex items-center justify-center text-center fixed top-0 left-0 w-full h-full bg-secondary z-50" data-testid="app-loader">
                    <div
                        suppressHydrationWarning
                        className="flex flex-col text-center items-center justify-center gap-2"
                    >
                        <Spinner />
                        {steps[currentStep]?.content}
                        {hasTimedOut && timeoutMessage && <Text>{timeoutMessage}</Text>}
                    </div>
                </div>
            )}
        </>
    )
}
